/**
 * Assessment List Page
 * Displays list of published assessments
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { assessmentsApi } from '../api/assessments.js';
import { Assessment, PaginatedResponse } from '../../types/models';
import { MediaImage } from '../components/MediaImage';

export const AssessmentList: React.FC = () => {
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });

  useEffect(() => {
    loadAssessments();
  }, []);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      setError(null);
      const result: PaginatedResponse<Assessment> = await assessmentsApi.list({
        status: 'published',
        page: pagination.page,
        limit: pagination.limit,
      });
      // Ensure data is an array and result structure is valid
      if (result && typeof result === 'object') {
        setAssessments(Array.isArray(result.data) ? result.data : []);
        setPagination(result.pagination || { page: 1, limit: 20, total: 0, total_pages: 0 });
      } else {
        console.error('Invalid API response structure:', result);
        setAssessments([]);
        setPagination({ page: 1, limit: 20, total: 0, total_pages: 0 });
      }
    } catch (err: any) {
      console.error('Error loading assessments:', err);
      const errorMessage = err?.message || err?.error || 'Failed to load assessments';
      setError(errorMessage);
      setAssessments([]); // Set empty array on error
      setPagination({ page: 1, limit: 20, total: 0, total_pages: 0 });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading assessments...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="assessment-list">
      <h1>Available Assessments</h1>
      {assessments.length === 0 ? (
        <p>No assessments available.</p>
      ) : (
        <div className="assessments-grid">
          {assessments.map((assessment) => (
            <div key={assessment.id} className="assessment-card">
              {assessment.image_url && (
                <div className="assessment-card-image">
                  <MediaImage
                    src={assessment.image_url}
                    alt={assessment.title}
                    width={assessment.image_width}
                    height={assessment.image_height}
                  />
                </div>
              )}
              <div className="assessment-card-content">
                <h3>{assessment.title}</h3>
                {assessment.description && <p>{assessment.description}</p>}
                <div className="assessment-meta">
                  {assessment.time_limit_seconds && (
                    <span>Time limit: {Math.floor(assessment.time_limit_seconds / 60)} minutes</span>
                  )}
                  {assessment.passing_score_percent && (
                    <span>Passing score: {assessment.passing_score_percent}%</span>
                  )}
                </div>
                <Link to={`/assessments/${assessment.id}`} className="btn btn-primary">
                  Start Assessment
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
